import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, StepLabel, Stepper, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import BundleAPI from 'api/orbitV1/BundleAPI'
import OrderAPI from 'api/orbitV1/OrderAPI'
import VoucherAPI from 'api/orbitV1/VoucherAPI'
import ColorBox from 'common/Box/ColorWhiteBox'
import PageLayout from 'common/Layout/Page/PageLayout'
import StyledStep from 'common/Stepper/StyledStep'
import { ColorBoxContent } from 'common/ui/colorBox'
import { useFormik } from 'formik'
import { generateValidationSchema } from 'helpers/formValidationSchema'
import OrbitSession from 'hooks/useCookies'
import { IBundlePlans } from 'types/api/v1/bundle.type'
import { IVoucherBundles } from 'types/api/v1/voucher.type'

import PlanDetailsCard from './components/PlanDetailsCard'
import ReviewOrder from './components/ReviewOrder'
import ReviewPayment from './components/ReviewPayment'
import SelectPlan from './components/SelectPlan'

function ProductCatalogueMain() {
  const [selectedIndexPlan, setSelection] = useState<number>(0)
  const [value, setValue] = useState<number>(0)
  const [activeStep, setActiveStep] = useState<number>(0)
  const [simsQuantity, setSimsQuantity] = useState<number | Array<number>>(1)
  const [page, setPage] = useState<number>(0)
  const [bundleProducts, setBundleProduct] = useState<IBundlePlans[]>([])
  const [selectedBundlePlan, setSelectedBundlePlan] = useState<IBundlePlans>()
  const [companyFormFilledStatus, setCompanyFormStatus] = useState(false)
  const [voucherData, setVoucherData] = useState<IVoucherBundles[]>([])
  const [voucherApplied, setVoucherApplied] = useState<IVoucherBundles | null>(
    null
  )
  const [hasCreated, setHasCreated] = useState(false)

  //final amount for purchase
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [finalAmount, setFinalAmount] = useState<number>(0)

  const [discountAmount, setDiscountAmount] = useState<number>(0)

  const orgId = OrbitSession.get('orgId')
  const userId = OrbitSession.get('_id')

  const formik = useFormik({
    initialValues: {
      email: '',
      companyName: '',
      uen: '',
      deliveryAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      },
      billingAddress: {
        blockNo: '',
        streetName: '',
        buildingName: '',
        postalCode: '',
        unitNo: ''
      }
    },
    validationSchema: generateValidationSchema(),
    onSubmit: (values) => {
      toast.error(JSON.stringify(values))
    }
  })

  const {
    status: getBundleProductStatus,
    data: allBundles,
    error
  } = useQuery(['getBundleProducts', 'productCatalogue'], async () => {
    const { data } = await BundleAPI.getbundleProducts(orgId)
    return data
  })

  const {
    status: getVoucherStatus,
    data: vouchersData,
    error: getVoucherError
  } = useQuery(['getVouchers', 'voucher'], async () => {
    const { data } = await VoucherAPI.getVouchers(orgId)
    return data
  })

  if (error || getVoucherError) {
    toast.error('Error loading data')
  }

  useEffect(() => {
    if (
      getBundleProductStatus === 'success' &&
      getVoucherStatus === 'success'
    ) {
      setVoucherData(vouchersData)
      setBundleProduct(allBundles)
      const firstBundle = allBundles
        .sort((a, b) => Number(a.price.value) - Number(b.price.value))
        .filter((data) => data.category === 'Circles One')

      setSelectedBundlePlan(firstBundle[0])
    }
  }, [getVoucherStatus, getBundleProductStatus])

  const goNextPage = () => {
    setActiveStep((step) => (step += 1))
    setPage((page) => (page += 1))
  }

  const goPreviousPage = () => {
    setActiveStep((step) => (step -= 1))
    setPage((page) => (page -= 1))
  }

  //TO DO: REMOVE PLAN ID - TO MOCK RESPONSE
  const handleCreate = async () => {
    const orderDetails = {
      // do not send newUser var so that no new order is created
      contactName: OrbitSession.get('userName'),
      orgId,
      orgName: formik.values.companyName,
      type: 'order',
      email: formik.values.email,
      //for demo purpose, hard coded plan value if not BE throw error
      plan: '631085df2a0d779e3ebd5a98',
      planName: selectedBundlePlan?.description,
      numberOfSims: Number(simsQuantity),
      price: finalAmount,
      itemName: `${Number(simsQuantity)} ${
        selectedBundlePlan?.description
      } SIMs`, // for credit card
      userId
    }
    try {
      if (!hasCreated) {
        await OrderAPI.createOrder(orderDetails)
        setHasCreated(true)
      }
    } catch (error) {
      toast.error('Error creating order.')
    }
  }
  //different views for different stages of selection
  //select plan --> review order(company details) --> review payment
  const pages = [
    <SelectPlan
      bundleData={bundleProducts}
      selectedIndexPlan={selectedIndexPlan}
      setSelection={setSelection}
      value={value}
      setValue={setValue}
      simsQuantity={simsQuantity}
      setSimsQuantity={setSimsQuantity}
      setSelectedBundlePlan={setSelectedBundlePlan}
      discountAmount={discountAmount}
      key='select-plan'
    />,
    <ReviewOrder
      formik={formik}
      key='review-order'
      orgId={orgId}
      setCompanyFormStatus={setCompanyFormStatus}
    />,
    <ReviewPayment
      key='review-payment'
      goPreviousPage={goPreviousPage}
      handleCreate={handleCreate}
      hasCreated={hasCreated}
    />
  ]

  return (
    <ColorBoxContent>
      <PageLayout header='Shop'>
        <ColorBox>
          <Box>
            <Typography
              variant='h3'
              sx={{
                color: 'primary.dark',
                textAlign: 'center'
              }}
            >
              Pricing
            </Typography>
            <Typography
              variant='caption'
              sx={{
                display: 'flex',
                justifyContent: 'center',
                color: 'neutrals.secondary',
                marginTop: '24px'
              }}
            >
              Get customized solution for your business. Easy to order and
              manage
            </Typography>

            <Box sx={{ display: 'flex', gap: '24px', marginTop: '32px' }}>
              <Box sx={{ width: '60%', minWidth: '360px' }}>
                <Stepper activeStep={activeStep} orientation='horizontal'>
                  {pages.map((_, index) => (
                    <StyledStep key={`stepper-${index}`}>
                      <StepLabel></StepLabel>
                    </StyledStep>
                  ))}
                </Stepper>
                {pages[page]}
              </Box>
              {selectedBundlePlan && (
                <Box>
                  <PlanDetailsCard
                    simsQuantity={simsQuantity}
                    selectedBundlePlan={selectedBundlePlan}
                    voucherData={voucherData}
                    setVoucherApplied={setVoucherApplied}
                    voucherApplied={voucherApplied}
                    setDiscountAmount={setDiscountAmount}
                    discountAmount={discountAmount}
                    setFinalAmount={setFinalAmount}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='subtitle1'>
                For further requirements please contact
              </Typography>
              <RouterLink
                to='/app/customer-support'
                style={{
                  fontSize: '16px',
                  color: '#0015C4',
                  textDecoration: 'none'
                }}
              >
                customer support
              </RouterLink>
            </Box>
            {page !== 2 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '60%'
                }}
              >
                <Box>
                  <Button
                    variant='secondary'
                    sx={{ borderRadius: '25px' }}
                    disabled={!page}
                    onClick={goPreviousPage}
                  >
                    Back
                  </Button>
                  <Button
                    data-cy='nextBtnShop'
                    disabled={
                      !simsQuantity ||
                      page === pages.length - 1 ||
                      (page === 1 && !companyFormFilledStatus)
                    }
                    variant='filled'
                    sx={{ borderRadius: '25px' }}
                    onClick={goNextPage}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </ColorBox>
      </PageLayout>
    </ColorBoxContent>
  )
}

export default ProductCatalogueMain
